import { formatDistanceToNow } from 'date-fns';

/** Human labels for tesseract team slugs; unknown slugs are shown as-is. */
export const TEAM_LABELS: Record<string, string> = {
  buildteam: 'Build Team',
  eventteam: 'Event Team',
  Newsletter: 'Newsletter',
  Curators: 'Curators',
};

export const teamLabel = (slug: string): string => TEAM_LABELS[slug] ?? slug;

/** Tailwind classes per team chip (falls back to accent). */
export const teamTone = (slug: string): string => {
  switch (slug) {
    case 'buildteam':
      return 'bg-orange-500/15 text-orange-300 border-orange-500/25';
    case 'eventteam':
      return 'bg-pink-500/15 text-pink-300 border-pink-500/25';
    case 'Newsletter':
      return 'bg-sky-500/15 text-sky-300 border-sky-500/25';
    case 'Curators':
      return 'bg-violet-500/15 text-violet-300 border-violet-500/25';
    default:
      return 'bg-accent-500/15 text-accent-300 border-accent-500/25';
  }
};

/**
 * Compacts a Users API playtime string such as `13d, 4h, 11m, 33s` to its two largest
 * units (`13d 4h`). Unknown formats are returned unchanged; empty input yields `—`.
 */
export function compactPlaytime(raw: string | null | undefined): string {
  if (!raw) return '—';
  const parts = raw
    .split(',')
    .map((p) => p.trim())
    .filter(Boolean);
  if (parts.length === 0) return raw;
  const meaningful = parts.filter((p) => !/^0[a-z]$/.test(p));
  return (meaningful.length > 0 ? meaningful : parts).slice(0, 2).join(' ');
}

/**
 * Parses a Users API join timestamp (ISO string, `YYYY-MM-DD HH:mm:ss`, or epoch
 * seconds/milliseconds). Returns null when absent or unparseable.
 */
export function parseJoinTime(raw: string | number | null | undefined): Date | null {
  if (raw === null || raw === undefined || raw === '') return null;
  if (typeof raw === 'number') {
    const ms = raw < 1e12 ? raw * 1000 : raw;
    const d = new Date(ms);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (/^\d+$/.test(raw)) return parseJoinTime(Number(raw));
  const d = new Date(raw.includes('T') || raw.includes('Z') ? raw : raw.replace(' ', 'T'));
  return Number.isNaN(d.getTime()) ? null : d;
}

/** `3 days ago` style label for a join timestamp, or `—`. */
export function formatJoinAgo(raw: string | number | null | undefined): string {
  const d = parseJoinTime(raw);
  return d ? formatDistanceToNow(d, { addSuffix: true }) : '—';
}

/** Coerces the Users API's string-or-number counters. */
export function asCount(value: string | number | null | undefined): number {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  const n = parseInt(value ?? '', 10);
  return Number.isFinite(n) ? n : 0;
}
